import Lenis from '@studio-freight/lenis';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import * as animePkg from 'animejs';
const anime = animePkg.default || animePkg;
import * as THREE from 'three';

gsap.registerPlugin(ScrollTrigger);

export function initAnimations() {
  // 1. Initialize Lenis (Smooth Scroll)
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    smoothWheel: true,
  });

  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // Smooth scroll for nav anchor links
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const target = this.getAttribute('href');
      if (target && target !== '#') {
        lenis.scrollTo(target);
      }
    });
  });

  // 2. Custom Cursor (GSAP quickTo)
  const cursor = document.getElementById('customCursor');
  if (cursor) {
    const xTo = gsap.quickTo(cursor, "x", {duration: 0.15, ease: "power3"});
    const yTo = gsap.quickTo(cursor, "y", {duration: 0.15, ease: "power3"});
    
    window.addEventListener("mousemove", (e) => {
      xTo(e.clientX);
      yTo(e.clientY);
    });

    document.querySelectorAll('a, button, .btn, input, textarea').forEach(el => {
      el.addEventListener('mouseenter', () => cursor.classList.add('hovering'));
      el.addEventListener('mouseleave', () => cursor.classList.remove('hovering'));
    });
  }

  // 3. Tracing Beam / Scroll Progress
  const beam = document.getElementById('tracingBeam');
  if (beam) {
    gsap.to(beam, {
      scaleX: 1,
      ease: "none",
      scrollTrigger: {
        trigger: document.body,
        start: "top top",
        end: "bottom bottom",
        scrub: true
      }
    });
  }

  // 4. Parallax Images in Cards
  const cardImages = document.querySelectorAll('.card__img');
  cardImages.forEach(img => {
    gsap.fromTo(img, 
      { y: '-8%' },
      { 
        y: '8%', 
        ease: 'none',
        scrollTrigger: {
          trigger: img.parentElement,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      }
    );
  });

  // 5. Advanced Text Reveals for Section Titles
  const titles = document.querySelectorAll('.section__title');
  titles.forEach(title => {
    // Split text into spans
    const text = title.textContent;
    title.innerHTML = '';
    text.split('').forEach(char => {
      const span = document.createElement('span');
      span.textContent = char === ' ' ? '\u00A0' : char;
      span.style.display = 'inline-block';
      title.appendChild(span);
    });

    gsap.fromTo(title.children, 
      { opacity: 0, y: 20 },
      { 
        opacity: 1, y: 0, 
        duration: 0.6,
        stagger: 0.02,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: title,
          start: 'top 90%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });

  // 6. Setup GSAP Scroll Reveal for elements with [data-reveal]
  const revealElements = document.querySelectorAll('[data-reveal]');
  revealElements.forEach((el) => {
    const animationType = el.getAttribute('data-reveal');
    gsap.fromTo(el, 
      {
        opacity: 0,
        y: animationType === 'fade-in' ? 30 : animationType === 'slide-in-up' ? 60 : 0,
        x: animationType === 'slide-in-left' ? -50 : animationType === 'slide-in-right' ? 50 : 0
      },
      {
        opacity: 1,
        y: 0,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 90%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });

  // 7. Magnetic Hover Effects (Spring Physics)
  const magneticItems = document.querySelectorAll('.btn, .contact-link');
  magneticItems.forEach(item => {
    item.addEventListener('mousemove', (e) => {
      const rect = item.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * 0.4;
      const y = (e.clientY - rect.top - rect.height / 2) * 0.4;
      gsap.to(item, { x, y, duration: 0.3, ease: 'power2.out' });
    });
    item.addEventListener('mouseleave', () => {
      gsap.to(item, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.3)' });
    });
  });
}

export function initThreeJSHero() {
  const canvas = document.getElementById('heroCanvas');
  if (!canvas) return;
  
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.position.z = 50;

  // Particle System
  const particlesGeometry = new THREE.BufferGeometry();
  const particlesCount = 700;
  
  const posArray = new Float32Array(particlesCount * 3);
  const originalPosArray = new Float32Array(particlesCount * 3);
  
  for(let i = 0; i < particlesCount * 3; i++) {
    const val = (Math.random() - 0.5) * 150;
    posArray[i] = val;
    originalPosArray[i] = val;
  }
  
  particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
  
  const rootStyle = getComputedStyle(document.documentElement);
  let accentColor = rootStyle.getPropertyValue('--accent-2').trim() || '#3b82f6';
  
  const particlesMaterial = new THREE.PointsMaterial({
    size: 0.5,
    color: accentColor,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending
  });
  
  const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
  scene.add(particlesMesh);

  // Mouse Interaction
  let mouseX = 0;
  let mouseY = 0;
  
  document.addEventListener('mousemove', (event) => {
    mouseX = (event.clientX / window.innerWidth) - 0.5;
    mouseY = (event.clientY / window.innerHeight) - 0.5;
  });

  const clock = new THREE.Clock();

  const tick = () => {
    const elapsedTime = clock.getElapsedTime();
    
    // Rotate particles globally
    particlesMesh.rotation.y = elapsedTime * 0.05;
    particlesMesh.rotation.x = elapsedTime * 0.02;
    
    // Interactive Particle Repulsion
    const positions = particlesGeometry.attributes.position.array;
    // Map mouse position to rough world space relative to camera
    const mx = mouseX * 100;
    const my = -mouseY * 100;
    
    for(let i = 0; i < particlesCount; i++) {
      const i3 = i * 3;
      const px = positions[i3];
      const py = positions[i3+1];
      
      const dx = mx - px;
      const dy = my - py;
      const dist = Math.sqrt(dx*dx + dy*dy);
      
      // If mouse is near, repel
      if (dist < 15) {
        const force = (15 - dist) / 15;
        positions[i3] -= (dx / dist) * force * 0.8;
        positions[i3+1] -= (dy / dist) * force * 0.8;
      }
      
      // Spring back to original positions
      positions[i3] += (originalPosArray[i3] - positions[i3]) * 0.02;
      positions[i3+1] += (originalPosArray[i3+1] - positions[i3+1]) * 0.02;
      positions[i3+2] += (originalPosArray[i3+2] - positions[i3+2]) * 0.02;
    }
    particlesGeometry.attributes.position.needsUpdate = true;
    
    // Mouse parallax for camera
    camera.position.x += (mouseX * 10 - camera.position.x) * 0.05;
    camera.position.y += (-mouseY * 10 - camera.position.y) * 0.05;
    camera.lookAt(scene.position);
    
    renderer.render(scene, camera);
    window.requestAnimationFrame(tick);
  };
  
  tick();
  
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });
  
  // Update color on theme change
  window.__canvasTick = () => {
    const newAccent = getComputedStyle(document.documentElement).getPropertyValue('--accent-2').trim();
    if(newAccent) {
       particlesMaterial.color.set(newAccent);
    }
  };
}
